import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AppNavbar from './AppNavbar.jsx';
import AppSidebar from './AppSidebar.jsx';
import AppFooter from './AppFooter.jsx';
import { readNavCollapsed, writeNavCollapsed } from './navModel.js';

/**
 * The chrome shared by every `_default-web-layout*` / `_users-groups-roles-layout`
 * shell: fixed-top navbar → page content → footer.
 *
 * Those four raw shells are byte-different only in which page-injection slots
 * their `<head>` carries (`<!-- page import style -->` vs. an inline
 * font-awesome `<link>`, and whether a second `<style><!-- page style --></style>`
 * block is present) — all of which are Vite/SCSS concerns in this project, not
 * layout structure. The rendered chrome is identical, so it lives here once and
 * each named layout component composes it (never folded into a single layout —
 * every design layout name keeps its own component).
 *
 * `children` is honoured when a page renders the layout directly; otherwise the
 * router's `<Outlet />` fills the shells' `<!-- main content -->` placeholder.
 */
export default function DefaultWebShell({ children }) {
  // The rail is permanent from `lg` up; below that it is a drawer the top bar's
  // toggle opens. Any navigation closes it, so a tap never leaves the drawer
  // covering the page you just asked for.
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setNavOpen(false), [location.pathname]);

  // Collapsed state is per-user and persisted: someone working in the
  // generators keeps the extra width across reloads instead of re-collapsing
  // the rail every visit.
  const [collapsed, setCollapsed] = useState(() =>
    readNavCollapsed(typeof window !== 'undefined' ? window.localStorage : undefined));

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      writeNavCollapsed(typeof window !== 'undefined' ? window.localStorage : undefined, next);
      return next;
    });
  };

  return (
    <div
      className={
        `app-shell app-shell--railed${navOpen ? ' app-shell--nav-open' : ''}` +
        `${collapsed ? ' app-shell--nav-collapsed' : ''}`
      }
    >
      <AppNavbar onToggleNav={() => setNavOpen((value) => !value)} navOpen={navOpen} />

      <div className="app-shell__body">
        <AppSidebar
          open={navOpen}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          onNavigate={() => setNavOpen(false)}
        />

        {/* Scrim: closes the drawer on tap, and only exists while it is open. */}
        {navOpen ? (
          <button
            type="button"
            className="app-shell__scrim"
            aria-label="Close navigation"
            onClick={() => setNavOpen(false)}
          />
        ) : null}

        {/* <!-- main content --> */}
        <main className="app-shell__main" id="main-content">
          {children ?? <Outlet />}
          <AppFooter />
        </main>
      </div>
    </div>
  );
}
